import { MenuItem, Stack, Typography } from '@mui/material';
import { ComponentType } from 'react';

import { ButtonWithPopover } from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { BoltIcon } from '@/components/icons/BoltIcon';
import { CheckIcon } from '@/components/icons/CheckIcon';
import { IconProps } from '@/components/icons/ChevronDownIcon';
import { ThinkingIcon } from '@/components/icons/ThinkingIcon';
import { COLORS } from '@/styles/tokens/colors';
import { ChatMode } from '@/types/schema';

import { useHouseholdChatContext } from '../contexts/householdChat.context';

interface ChatModeMeta {
  Icon: ComponentType<IconProps>;
  label: string;
  description: string;
}

/**
 * Display metadata for each answer-depth mode (icon, label, and copy).
 */
const CHAT_MODE_META: Record<ChatMode, ChatModeMeta> = {
  [ChatMode.Quick]: {
    Icon: BoltIcon,
    label: 'Fast',
    description: 'Quick answers for straightforward questions',
  },
  [ChatMode.Deep]: {
    Icon: ThinkingIcon,
    label: 'Thinking',
    description: 'Reasons through the full plan and shows sources',
  },
};

// Render order for the two modes (Fast first, then Thinking).
const CHAT_MODE_ORDER: ChatMode[] = [ChatMode.Quick, ChatMode.Deep];

/**
 * Lets the user choose how thoroughly the assistant answers the next message.
 * Rendered as a compact dropdown: the trigger shows the active mode, and the
 * menu lists each mode with its icon, label, and a one-line description.
 *
 * The selection is sourced from (and written back to) the chat context, so it
 * applies to both typed messages and suggestion chips. Selecting or starting
 * another conversation re-syncs it with that conversation's saved mode.
 */
export function HouseholdChatModeSelector() {
  const { chatMode, setChatMode } = useHouseholdChatContext();
  const active = CHAT_MODE_META[chatMode];

  return (
    <ButtonWithPopover
      aria-label={`Answer depth: ${active.label}`}
      // Ghost, compact trigger so it reads as a subtle toolbar control next to
      // the Ask button rather than a second primary affordance.
      variant="transparent"
      size="xs"
      buttonSx={{ maxHeight: '20px', py: 0 }}
      startIcon={active.Icon}
      // Pin both the mode icon and the chevron to 16px; without an explicit
      // size, Button uses the passed iconProps as-is and the icons fall back
      // to the icon default (24px) rather than the button size. Tint them to
      // GRAY[500] so they match the label text instead of the darker default.
      iconProps={{ 'aria-hidden': true, size: 16, color: COLORS.GRAY[500] }}
      label={<Typography variant="body-xs">{active.label}</Typography>}
      popperVariant="menuAbove"
      // The composer sits at the bottom of the panel, so open the menu above
      // the trigger: anchor the menu's bottom-left to the button's top-left so
      // it stacks on top of the button instead of overlapping it.
      popperProps={{
        anchorOrigin: { vertical: 'top', horizontal: 'left' },
        transformOrigin: { vertical: 'bottom', horizontal: 'left' },
      }}
    >
      {CHAT_MODE_ORDER.map((mode) => {
        const { Icon, label, description } = CHAT_MODE_META[mode];
        const isSelected = mode === chatMode;
        return (
          <MenuItem key={mode} onClick={() => setChatMode(mode)}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              // MenuItem forces white-space: nowrap; reset it so the
              // descriptions wrap within the menu instead of being clipped.
              sx={{ maxWidth: 280, py: 0.25, whiteSpace: 'normal' }}
            >
              <Icon size={16} aria-hidden sx={{ color: COLORS.NAVY[900] }} />
              <Stack spacing={0.25} sx={{ flexGrow: 1 }}>
                <Typography
                  variant="body-xs"
                  sx={{
                    fontWeight: isSelected ? 600 : 500,
                    color: COLORS.NAVY[900],
                  }}
                >
                  {label}
                </Typography>
                <Typography variant="body-xs">{description}</Typography>
              </Stack>
              {isSelected && (
                <CheckIcon
                  size={16}
                  aria-hidden
                  sx={{ alignSelf: 'center', color: COLORS.NAVY[900] }}
                />
              )}
            </Stack>
          </MenuItem>
        );
      })}
    </ButtonWithPopover>
  );
}
